import React from 'react';
import { FaRocket, FaUsers, FaCalendarAlt, FaFileInvoiceDollar, FaChartLine, FaTimes } from 'react-icons/fa';

const features = [
  { icon: FaUsers, text: 'Franchisee and client management' },
  { icon: FaCalendarAlt, text: 'Job scheduling and tracking' },
  { icon: FaFileInvoiceDollar, text: 'Invoicing and billing' },
  { icon: FaChartLine, text: 'Activity logs' },
];

/**
 * WelcomeModal — shown once to new users on their first visit to the dashboard.
 *
 * Props:
 *   isOpen   {boolean}  — controls visibility
 *   onClose  {function} — called when the user dismisses the modal
 *   firstName {string}  — user's first name for personalisation
 */
const WelcomeModal = ({ isOpen, onClose, firstName }) => {
  if (!isOpen) return null;

  const displayName = firstName ? `, ${firstName}` : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Header strip */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 px-6 pt-8 pb-10 text-white">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close welcome modal"
          >
            <FaTimes className="h-5 w-5" />
          </button>

          <div className="flex items-center justify-center h-14 w-14 rounded-full bg-white/20 mb-4">
            <FaRocket className="h-7 w-7 text-white" />
          </div>
          <h2
            id="welcome-modal-title"
            className="text-2xl font-extrabold tracking-tight"
          >
            Welcome{displayName}!
          </h2>
          <p className="mt-1 text-green-100 text-sm">
            Your Loope demo account is ready to explore.
          </p>
        </div>

        {/* Body — pulled up to overlap the header strip */}
        <div className="relative -mt-4 bg-white rounded-t-2xl px-6 pt-6 pb-8">
          <p className="text-sm text-gray-600 mb-5">
            This is a shared demo environment. Feel free to explore — here's what you can do:
          </p>

          <ul className="space-y-3 mb-7">
            {features.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-green-50">
                  <Icon className="h-4 w-4 text-green-600" />
                </span>
                {text}
              </li>
            ))}
          </ul>

          <button
            onClick={onClose}
            className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-3 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Get started
          </button>

          <p className="mt-4 text-center text-xs text-gray-400">
            Demo data may be reset periodically. Your account details are private and not visible to other demo users.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
